/**
 * NewsletterSignup — public email capture for the Follow-Through Letter.
 * Double opt-in: success state tells the visitor to check their inbox.
 * `source` tags where on the funnel the address came from (landing_letter,
 * landing_magnet, ...) for later attribution.
 */

import { useState } from 'react';
import { Mail, Loader2, MailCheck } from 'lucide-react';
import { subscribeNewsletter } from '../lib/api';
import { track } from '../lib/analytics';

export default function NewsletterSignup({ source = 'landing_letter', onSuccess }) {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — real users never fill this
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      await subscribeNewsletter(value, source, website);
      track('newsletter_subscribed', { source });
      setState('done');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div className="flex items-center justify-center gap-2.5 rounded-lg border border-green-800/40 bg-green-950/20 px-4 py-3.5 max-w-md mx-auto">
        <MailCheck size={18} className="text-green-400 flex-shrink-0" />
        <p className="text-sm text-green-200 text-left">
          Almost in — check your inbox and click the confirmation link.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md mx-auto">
      {/* Honeypot: off-screen, not tabbable, hidden from screen readers.
          Bots that fill every field trip it; the backend silently drops them. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-slate-800/60 border border-slate-700 rounded-lg pl-9 pr-3 py-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500/60 min-h-tap"
          />
        </div>
        <button
          type="submit"
          disabled={state === 'sending'}
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white rounded-lg px-5 py-3 text-sm font-medium min-h-tap transition-colors whitespace-nowrap"
        >
          {state === 'sending' ? <Loader2 size={15} className="animate-spin" /> : null}
          Get the Letter
        </button>
      </div>
      {error && <p className="text-xs text-red-400 mt-2 text-left">{error}</p>}
      <p className="text-[11px] text-slate-600 mt-2.5">
        One email a week. Unsubscribe in one click, any time.
      </p>
    </form>
  );
}
